import React, { useEffect, useRef, useState, useMemo } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import { connect } from "react-redux";
import styled, { keyframes } from "styled-components";
import { SmallLogoWithDashboard } from "../logo/SmallLogo";
import LogoutWidget from "../logout/LogoutWidget";
import NotificationOverlay from "../status/NotificationOverlay";
import { UserData } from "../model/user";
import { ApplicationState } from "../reducers";
import { bindActionCreators, Dispatch } from "redux";
import { setUserAction, setUserDataAction } from "../loginReducer";
import { OrganisationInfo } from "../services/adminService";
import { getOrganisationInfo } from "../organisation/firestore/organisationInfo";
import { db } from "..";
import "./Log.scss";

interface LogEntry {
    id: string;
    level: string;
    code: string;
    msg: string;
    deviceId: string;
    tag: string;
    timestamp: string;
}

const levelColor = (level: string): string => {
    switch (level?.toUpperCase()) {
        case "ERROR": return "#ef4444";
        case "WARN":
        case "WARNING": return "#f59e0b";
        case "DEBUG": return "#9ca3af";
        default: return "inherit";
    }
};

const LevelBadge = styled.span<{ level: string }>`
    color: ${({ level }) => levelColor(level)};
    font-weight: bold;
    font-family: monospace;
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
`;

const LiveBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.75rem;
    color: #10b981;
    font-weight: 600;
    margin-left: 1rem;
    &::before {
        content: '';
        display: inline-block;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background: #10b981;
        animation: ${pulse} 2s ease-in-out infinite;
    }
`;

const LogPage = (props: Props) => {
    // @ts-ignore
    const urlParams = new URLSearchParams(props.location.search);
    const organisationId: string = urlParams.get("organisation-id") || "";
    const initialDeviceId: string = urlParams.get("device-id") || "";

    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [deviceIdFilter, setDeviceIdFilter] = useState(initialDeviceId);
    const [deviceIdInput, setDeviceIdInput] = useState(initialDeviceId);
    const [tagFilter, setTagFilter] = useState("");
    const allLevels = ["ERROR", "WARN", "DEBUG", "INFO"];
    const [enabledLevels, setEnabledLevels] = useState<string[]>(allLevels);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isLive, setIsLive] = useState(false);
    const [codeFilter, setCodeFilter] = useState<{ code: string; mode: "include" | "exclude" } | null>(null);
    const [codePopup, setCodePopup] = useState<{ code: string; x: number; y: number } | null>(null);
    const [fetchLimit, setFetchLimit] = useState(200);
    const [queryError, setQueryError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (organisationId && props.jwtToken) {
            getOrganisationInfo(organisationId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.jwtToken]);

    useEffect(() => {
        if (!organisationId) return;

        setLogs([]);
        setQueryError(null);
        setLoading(true);
        setIsLive(true);

        let query: firebase.firestore.Query = db
            .doc("commander")
            .collection("organisations")
            .doc(organisationId)
            .collection("logs");

        // Apply at most one server-side filter to avoid composite index explosion.
        // Precedence: code exclude > level > device
        if (codeFilter?.mode === "exclude") {
            query = query.where("code", "!=", codeFilter.code);
            query = query.orderBy("code");
        } else if (enabledLevels.length > 0 && enabledLevels.length < allLevels.length) {
            const firestoreLevels = enabledLevels.flatMap(l =>
                l === "WARN"
                    ? ["WARN", "warn", "WARNING", "warning", "Warning"]
                    : [l, l.toLowerCase(), l.charAt(0) + l.slice(1).toLowerCase()]
            );
            query = query.where("level", "in", firestoreLevels);
        } else if (deviceIdFilter) {
            query = query.where("deviceId", "==", deviceIdFilter);
        }

        query = query.orderBy("timestamp", "desc").limit(fetchLimit);

        const unsubscribe = query.onSnapshot((snapshot) => {
            const entries: LogEntry[] = snapshot.docs.map((doc) => ({
                id: doc.id,
                level: doc.data().level || "",
                code: doc.data().code || "",
                msg: doc.data().msg || "",
                deviceId: doc.data().deviceId || "",
                tag: doc.data().tag || "",
                timestamp: doc.data().timestamp || "",
            }));
            // Sort ascending by timestamp so newest entries appear at the bottom
            entries.sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1));
            setLogs(entries);
            setLoading(false);
            setQueryError(null);
        }, (err) => {
            console.error("Logs query failed:", err);
            setLoading(false);
            setQueryError(err.message);
        });

        return () => {
            setIsLive(false);
            unsubscribe();
        };
    }, [organisationId, deviceIdFilter, enabledLevels, codeFilter, fetchLimit]);

    const filteredLogs = useMemo(() => {
        return logs.filter((log) => {
            if (deviceIdFilter && log.deviceId !== deviceIdFilter) return false;
            if (enabledLevels.length < allLevels.length) {
                let level = log.level?.toUpperCase();
                if (level === "WARNING") level = "WARN";
                if (!enabledLevels.includes(level)) return false;
            }
            if (codeFilter) {
                if (codeFilter.mode === "include" && log.code !== codeFilter.code) return false;
                if (codeFilter.mode === "exclude" && log.code === codeFilter.code) return false;
            }
            if (startDate) {
                const logTime = new Date(log.timestamp).getTime();
                const startTime = new Date(startDate).getTime();
                if (isNaN(logTime) || logTime < startTime) return false;
            }
            if (endDate) {
                const logTime = new Date(log.timestamp).getTime();
                const endTime = new Date(endDate).getTime();
                if (isNaN(logTime) || logTime > endTime) return false;
            }
            return true;
        });
    }, [logs, deviceIdFilter, enabledLevels, codeFilter, startDate, endDate]);

    // Auto-expand fetch limit when filters leave too few results
    const MIN_VISIBLE = 20;
    const MAX_FETCH = 5000;
    useEffect(() => {
        if (filteredLogs.length < MIN_VISIBLE && logs.length >= fetchLimit && fetchLimit < MAX_FETCH) {
            setFetchLimit((prev) => Math.min(prev * 3, MAX_FETCH));
        }
    }, [filteredLogs.length, logs.length, fetchLimit]);

    // Reset fetch limit when filters are loosened
    useEffect(() => {
        setFetchLimit(1000);
    }, [deviceIdFilter, tagFilter, enabledLevels, codeFilter]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "instant" as ScrollBehavior });
    }, [filteredLogs]);

    return (
        <div className="Dashboard">
            <div className="Dashboard-header">
                <SmallLogoWithDashboard fontSizeRem={1.625} />
                <LogoutWidget {...props} />
            </div>
            <NotificationOverlay />
            <div className="Log-content">
                <div className="Log-title-row">
                    <h1>Logs — {props.organisationInfo?.name ?? organisationId}</h1>
                    {isLive && <LiveBadge>LIVE</LiveBadge>}
                </div>
                <div className="Log-filter-bar">
                    <input
                        className="Log-filter-input"
                        type="text"
                        placeholder="Device ID"
                        value={deviceIdInput}
                        onChange={(e) => setDeviceIdInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") setDeviceIdFilter(deviceIdInput.trim());
                        }}
                        onBlur={() => setDeviceIdFilter(deviceIdInput.trim())}
                    />
                    <label className="Log-filter-label">
                        From
                        <input
                            className="Log-filter-input"
                            type="datetime-local"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </label>
                    <label className="Log-filter-label">
                        To
                        <input
                            className="Log-filter-input"
                            type="datetime-local"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </label>
                    <div className="Log-level-toggles">
                        {allLevels.map((level) => (
                            <label key={level} className="Log-level-toggle">
                                <input
                                    type="checkbox"
                                    checked={enabledLevels.includes(level)}
                                    onChange={() => {
                                        setEnabledLevels((prev) =>
                                            prev.includes(level)
                                                ? prev.filter((l) => l !== level)
                                                : [...prev, level]
                                        );
                                    }}
                                />
                                <span style={{ color: levelColor(level), fontWeight: 600 }}>
                                    {level}
                                </span>
                            </label>
                        ))}
                    </div>
                    {tagFilter && (
                        <span className="Log-active-filter">
                            Tag: {tagFilter}
                            <span className="Log-active-filter-x" onClick={() => setTagFilter("")}>&times;</span>
                        </span>
                    )}
                    {codeFilter && (
                        <span className="Log-active-filter">
                            Code: {codeFilter.mode === "include" ? "" : "NOT "}{codeFilter.code}
                            <span className="Log-active-filter-x" onClick={() => setCodeFilter(null)}>&times;</span>
                        </span>
                    )}
                    <button
                        className="Log-clear-button"
                        onClick={() => {
                            setDeviceIdFilter("");
                            setDeviceIdInput("");
                            setTagFilter("");
                            setCodeFilter(null);
                            setCodePopup(null);
                            setEnabledLevels(allLevels);
                            setStartDate("");
                            setEndDate("");
                        }}
                    >
                        Clear filters
                    </button>
                </div>
                {queryError && (
                    <div className="Log-query-error">
                        Query requires a composite index. Check the browser console for a link to create it.
                    </div>
                )}
                {loading ? (
                    <div className="Log-spinner-container">
                        <div className="Log-spinner" />
                    </div>
                ) : (
                    <div className="Log-table-container">
                        <table className="Log-table">
                            <thead>
                                <tr>
                                    <th>Timestamp</th>
                                    <th>Level</th>
                                    <th>Device</th>
                                    <th>Tag</th>
                                    <th>Code</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLogs.map((log) => (
                                    <tr key={log.id}>
                                        <td className="Log-ts">{log.timestamp}</td>
                                        <td className="Log-level">
                                            <LevelBadge level={log.level}>{log.level}</LevelBadge>
                                        </td>
                                        <td
                                            className="Log-device Log-device--clickable"
                                            title={log.deviceId}
                                            onClick={() => {
                                                setDeviceIdFilter(log.deviceId);
                                                setDeviceIdInput(log.deviceId);
                                            }}
                                        >{log.deviceId}</td>
                                        <td
                                            className="Log-tag Log-tag--clickable"
                                            onClick={() => setTagFilter(log.tag)}
                                        >{log.tag}</td>
                                        <td
                                            className="Log-code Log-code--clickable"
                                            onClick={(e) => {
                                                if (log.code) {
                                                    setCodePopup({ code: log.code, x: e.clientX, y: e.clientY });
                                                }
                                            }}
                                        >{log.code}</td>
                                        <td className="Log-msg">{log.msg}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div ref={bottomRef} />
                    </div>
                )}
            </div>
            {codePopup && (
                <div className="Log-code-popup-overlay" onClick={() => setCodePopup(null)}>
                    <div
                        className="Log-code-popup"
                        style={{ left: codePopup.x, top: codePopup.y }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button onClick={() => { setCodeFilter({ code: codePopup.code, mode: "include" }); setCodePopup(null); }}>
                            Only {codePopup.code}
                        </button>
                        <button onClick={() => { setCodeFilter({ code: codePopup.code, mode: "exclude" }); setCodePopup(null); }}>
                            Exclude {codePopup.code}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

interface StateToProps {
    jwtToken: string | undefined;
    user: firebase.User | undefined;
    userData: UserData | undefined;
    organisationInfo: OrganisationInfo | undefined;
}

interface DispatchFromProps {
    setUser: (_: firebase.User | undefined) => void;
    setUserData: (_: UserData | undefined) => void;
}

interface Props extends StateToProps, DispatchFromProps {}

const mapStateToProps = (state: ApplicationState) => ({
    jwtToken: state.login.jwtToken,
    user: state.login.user,
    userData: state.login.userData,
    organisationInfo: state.organisation.organisationInfo,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            setUser: setUserAction,
            setUserData: setUserDataAction,
        },
        dispatch,
    );

export default connect<StateToProps, DispatchFromProps>(
    // @ts-ignore
    mapStateToProps,
    mapDispatchToProps,
)(LogPage);
